import React, { useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { ciApi } from "../ciApi";
import { Panel, Kicker, DataTable, ConfBadge } from "../ciWidgets";

const CheckIcon = ({ status }) =>
  status === "pass" ? (
    <CheckCircle2 className="w-4 h-4 text-emerald-300 shrink-0" />
  ) : status === "fail" ? (
    <XCircle className="w-4 h-4 text-red-300 shrink-0" />
  ) : (
    <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0" />
  );

export const EvidenceTab = ({ report, onReload }) => {
  const ver = report.verification || {};
  const ev = report.evidence_register || [];
  const cls = report.classification || {};
  const ass = report.assurance || {};
  const [attributing, setAttributing] = useState(false);

  const attributeSources = async () => {
    setAttributing(true);
    try {
      const r = await ciApi.post(`/reports/${report.id}/attribute-sources`);
      toast.success(`${r.data.attributed} citation(s) attributed — confirm before external use`);
      onReload && (await onReload());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Source attribution failed — please retry");
    } finally {
      setAttributing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ci-tab-evidence">
      {(ass.checks || []).length > 0 && (
        <Panel className="p-7" data-testid="ci-assurance-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <Kicker>Automated quality checks — system-run</Kicker>
            {(ass.sources_downgraded || 0) > 0 && (
              <button
                onClick={attributeSources}
                disabled={attributing}
                data-testid="ci-attribute-sources-btn"
                className="inline-flex items-center gap-2 rounded-full bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-[#04211c] font-bold text-[12px] px-4 py-2 transition-colors"
              >
                {attributing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {attributing ? "Attributing sources… (~30s)" : `Strengthen ${ass.sources_downgraded} weak source(s) with AI attribution`}
              </button>
            )}
          </div>
          <p className="text-[12.5px] text-white/50 mb-5 max-w-4xl">
            These controls run automatically on every generated report: source grading, unit &amp; scale consistency, date currency,
            value-pool reconciliation and peer-percentile evidence checks. Results are published unedited.
          </p>
          {(ass.verification_fail_count || 0) > 0 && (
            <div className="mb-4 rounded-lg border border-amber-400/25 bg-amber-400/[0.05] px-4 py-2.5 text-[12px] text-amber-200/90" data-testid="ci-verification-fails-note">
              {ass.verification_fail_count} automated consistency check(s) flagged items for management follow-up — details are consolidated in the annexure below.
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-2.5">
            {ass.checks.map((c, i) => (
              <div key={i} className="flex items-start gap-3 rounded-lg border border-white/8 bg-[#060d18] px-4 py-3" data-testid={`ci-assurance-check-${i}`}>
                <CheckIcon status={c.status} />
                <div>
                  <div className="text-[13px] text-white/80 font-medium">{c.check}</div>
                  {c.detail && <div className="text-[12px] text-white/45 leading-relaxed mt-0.5">{c.detail}</div>}
                </div>
              </div>
            ))}
          </div>
          {(ass.unit_warnings || []).length > 0 && (
            <div className="mt-4 rounded-lg border border-red-400/25 bg-red-400/[0.05] px-4 py-3">
              <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-red-300 mb-2">Unit / scale warnings</div>
              <ul className="space-y-1.5">
                {ass.unit_warnings.map((x, i) => (
                  <li key={i} className="text-[12px] text-white/65">· {x}</li>
                ))}
              </ul>
            </div>
          )}
          {(ass.date_flags || []).length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-400/25 bg-amber-400/[0.05] px-4 py-3">
              <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-amber-300 mb-2">Date-currency flags</div>
              <ul className="space-y-1.5">
                {ass.date_flags.map((x, i) => (
                  <li key={i} className="text-[12px] text-white/65">· {x}</li>
                ))}
              </ul>
            </div>
          )}
        </Panel>
      )}

      <Panel className="p-7">
        <Kicker className="mb-2">Automated evidence-consistency check — second-line control, not independent assurance</Kicker>
        {ver.overall_assessment && <p className="text-[14px] text-white/70 leading-relaxed mb-5 max-w-4xl">{ver.overall_assessment}</p>}
        <div className="grid md:grid-cols-2 gap-2.5">
          {(ver.checks || []).map((c, i) => (
            <div key={i} className="flex items-start gap-3 rounded-lg border border-white/8 bg-[#060d18] px-4 py-3">
              <CheckIcon status={c.status} />
              <div>
                <div className="text-[13px] text-white/80 font-medium">{c.check}</div>
                {c.detail && <div className="text-[12px] text-white/45 leading-relaxed mt-0.5">{c.detail}</div>}
              </div>
            </div>
          ))}
        </div>
        {(ver.issues || []).length > 0 && (
          <div className="mt-4 rounded-lg border border-red-400/25 bg-red-400/[0.05] px-4 py-3">
            <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-red-300 mb-2">Material issues flagged</div>
            <ul className="space-y-1.5">
              {ver.issues.map((x, i) => (
                <li key={i} className="text-[12px] text-white/65">· {x}</li>
              ))}
            </ul>
          </div>
        )}
      </Panel>

      <Panel className="p-7">
        <Kicker className="mb-4">Sources analysed</Kicker>
        <DataTable
          testId="ci-sources-table"
          headers={["File", "Category", "Extracted text"]}
          rows={(report.sources_used || []).map((s) => [
            <span className="font-medium text-white">{s.filename}</span>,
            <span className="text-teal-300 text-[12px]">{s.category}</span>,
            <span className="tabular-nums">{(s.chars || 0).toLocaleString()} chars</span>,
          ])}
        />
        {(report.sources_used || []).length === 0 && (
          <div className="text-[13px] text-white/40 italic">
            No documents were uploaded — the analysis relies on credible public knowledge, with confidence downgraded accordingly.
          </div>
        )}
      </Panel>

      <Panel className="p-7">
        <Kicker className="mb-4">Consolidated evidence register ({ev.length} entries)</Kicker>
        <div className="max-h-[480px] overflow-y-auto">
          <DataTable
            testId="ci-evidence-register-table"
            headers={["ID", "Statement", "Type", "Dimension", "Source", "Period", "Confidence", "Limitations"]}
            rows={ev.map((e) => [
              <span className="text-teal-300 tabular-nums text-[12px]">{e.id}</span>,
              <span className="text-[12px]">{e.statement}</span>,
              <span className="text-[11px] text-white/50">{(e.statement_type || "").replace(/_/g, " ")}</span>,
              <span className="text-[11px]">{e.dimension}</span>,
              <span className="text-[12px]">
                {e.source_title}
                {(e.source_quality === "generic" || e.source_quality === "missing") && (
                  <span className="block text-[10px] text-amber-300">generic source — auto-downgraded</span>
                )}
                {e.source_quality === "attributed" && (
                  <span className="block text-[10px] text-teal-300">AI-attributed — confirm before external use</span>
                )}
              </span>,
              e.period,
              <ConfBadge level={e.confidence} />,
              <span className="text-[11px] text-white/40">{e.limitations}</span>,
            ])}
          />
        </div>
      </Panel>

      <div className="grid md:grid-cols-3 gap-6">
        {[
          ["Assumptions", ver.assumptions],
          ["Limitations", ver.limitations],
          ["Required management inputs", ver.required_management_inputs],
        ].map(([t, items]) => (
          <Panel key={t} className="p-6">
            <Kicker className="mb-3">{t}</Kicker>
            <ul className="space-y-2">
              {(items || []).map((x, i) => (
                <li key={i} className="text-[12px] text-white/60 leading-relaxed">· {x}</li>
              ))}
              {(items || []).length === 0 && <li className="text-[12px] text-white/35 italic">None recorded</li>}
            </ul>
          </Panel>
        ))}
      </div>

      {((cls.data_gaps || []).length > 0 || (cls.conflicting_data || []).length > 0) && (
        <div className="grid md:grid-cols-2 gap-6">
          <Panel className="p-6">
            <Kicker className="mb-3">Data gaps identified by the Orchestrator</Kicker>
            <ul className="space-y-2">
              {(cls.data_gaps || []).map((x, i) => (
                <li key={i} className="text-[12px] text-white/60 leading-relaxed">· {x}</li>
              ))}
            </ul>
          </Panel>
          <Panel className="p-6">
            <Kicker className="mb-3">Conflicting data disclosed</Kicker>
            <ul className="space-y-2">
              {(cls.conflicting_data || []).map((x, i) => (
                <li key={i} className="text-[12px] text-white/60 leading-relaxed">· {x}</li>
              ))}
              {(cls.conflicting_data || []).length === 0 && <li className="text-[12px] text-white/35 italic">No conflicts disclosed</li>}
            </ul>
          </Panel>
        </div>
      )}

      {report.gaps_appendix && (
        <Panel className="p-7 border-amber-400/20" data-testid="ci-gaps-appendix">
          <Kicker className="mb-2 text-amber-300">{report.gaps_appendix.title || "Annexure — data gaps & items for management confirmation"}</Kicker>
          {report.gaps_appendix.note && (
            <p className="text-[12.5px] text-white/55 leading-relaxed mb-5 max-w-4xl">{report.gaps_appendix.note}</p>
          )}
          {(report.gaps_appendix.data_gaps || []).length > 0 && (
            <div className="mb-5">
              <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-white/50 mb-2.5">Consolidated data gaps by dimension</div>
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {report.gaps_appendix.data_gaps.map((g, i) => (
                  <div key={i} className="flex gap-2.5 text-[12px] text-white/60 leading-relaxed">
                    <span className="w-1 h-1 rounded-full bg-amber-300 mt-2 shrink-0" />
                    <span><b className="text-white/75">{g.dimension}:</b> {g.gap}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(report.gaps_appendix.follow_ups || []).length > 0 && (
            <div>
              <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-white/50 mb-2.5">Items for management confirmation</div>
              <ul className="space-y-2">
                {report.gaps_appendix.follow_ups.map((x, i) => (
                  <li key={i} className="text-[12px] text-white/60 leading-relaxed">· {x}</li>
                ))}
              </ul>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
};
